import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Input, LinearProgress, Modal, ModalClose, ModalDialog, Option, Select, Stack, Table, Typography } from '@mui/joy';
import { newPayrollRequestId, payrollRequest } from '../../payrollApi';
import { configLabel, copy, dayLabel, employeeDiff, money, FIELD_LABELS, validatePatch } from './payrollUtils';
import { RulesView } from './PayrollHistory';
import { dialogSx, payrollSurfaceSx, sectionSx, tableSx } from './payrollStyles';

const LABELS = { today: 'С сегодняшнего дня', from_date: 'С выбранной даты', all: 'За всю историю' };
const RUNNING = ['sending', 'polling', 'retry'];
function restore(key) {
    try {
        if (key.endsWith(':undefined') || key.endsWith(':null')) return null;
        const data = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (data?.version !== 1 || !['preview', 'apply'].includes(data.kind) || !data.body?.request_id) return null;
        return { ...data, phase: data.jobId ? 'polling' : 'retry' };
    } catch { return null; }
}

function ResultView({ result, locations }) {
    const [limit, setLimit] = useState(100);
    const rows = useMemo(() => employeeDiff(result), [result]);
    const names = id => locations.find(item => item.id === id)?.name || `Точка №${id}`;
    return <Stack spacing={1.5}>
        <Typography>Точки: {(result.locations || []).map(names).join(', ') || 'Нет привязанных точек'}. Дней с записями: {result.days}.</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
            {[["Было", result.before_total], ["Станет", result.after_total], ["Разница", result.delta]].map(([label, value]) =>
                <Box key={label} sx={{ p: 1.5, bgcolor: 'var(--joy-palette-background-level2)', borderRadius: '12px', border: '1px solid var(--joy-palette-divider)' }}>
                    <Typography level="body-sm">{label}</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{money(value)}</Typography>
                </Box>)}
        </Box>
        {result.days === 0 && <Alert color="neutral">На затронутые даты пока нет начислений. Правила всё равно будут сохранены для будущих расчётов.</Alert>}
        {!!result.overwritten_boundaries?.length && <Alert color="warning">Будут заменены затронутые условия на границах: {result.overwritten_boundaries.map(dayLabel).join(', ')}.</Alert>}
        <Box tabIndex={0} role="region" aria-label="Таблица изменения начислений, прокрутка по горизонтали" sx={{ overflowX: 'auto', border: '1px solid var(--joy-palette-divider)', borderRadius: '12px' }}><Table size="sm" sx={tableSx} aria-label="Изменение начислений">
            <thead><tr><th>Дата / точка</th><th>Сотрудник</th><th>Было</th><th>Станет</th><th>Разница</th></tr></thead>
            <tbody>{rows.slice(0, limit).map(row => <tr key={row.key}>
                <td>{row.date}<br />{names(row.location)}</td><td>{row.name}</td>
                <td>{money(row.before)}</td><td>{money(row.after)}</td><td>{money(row.delta)}</td>
            </tr>)}</tbody>
        </Table></Box>
        {rows.length > limit && <Button variant="plain" onClick={() => setLimit(value => value + 100)}>Ещё 100 строк ({rows.length - limit} осталось)</Button>}
        {['before', 'after'].map(side => <Box component="details" key={side}>
            <Typography component="summary">{side === 'before' ? 'История до изменения' : 'История после изменения'}</Typography>
            {(result[`timeline_${side}`] || []).map(([day, value]) => <Box key={day} sx={{ my: 1 }}>
                <Typography fontWeight="lg">С {dayLabel(day)}</Typography>
                {value && typeof value === 'object' ? <RulesView payload={value} />
                    : <Typography>{value === null ? 'Автоматические условия отключены' : `Конфигурация №${value}`}</Typography>}
            </Box>)}
        </Box>)}
    </Stack>;
}

export default function PayrollChangePanel({ token, accountId, canEdit, configs, locations,
    meta, draft, onDraftClose, onApplied, onReload, onLockChange }) {
    const storageKey = `photo-payroll-operation-v1:${accountId}`;
    const [operation, setOperation] = useState(() => restore(storageKey));
    const [visible, setVisible] = useState(() => !!restore(storageKey));
    const [mode, setMode] = useState('today'), [date, setDate] = useState('');
    const [confirm, setConfirm] = useState(false), [error, setError] = useState('');
    const [locationId, setLocationId] = useState(''), [configId, setConfigId] = useState('');
    const [bindings, setBindings] = useState(null), [bindingError, setBindingError] = useState('');
    const [historyAttempt, setHistoryAttempt] = useState(0), [tick, setTick] = useState(Date.now());
    const busy = useRef(false), alive = useRef(true), done = useRef(null);
    const latest = useRef({ onApplied, onDraftClose });
    latest.current = { onApplied, onDraftClose };
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    useEffect(() => {
        if (!draft) return;
        if (operation) {
            setVisible(true); latest.current.onDraftClose();
            return;
        }
        setOperation({ phase: 'editing', basis: copy(meta), change: copy(draft.change), availablePatch: draft.availablePatch ? copy(draft.availablePatch) : null, title: draft.title });
        setMode('today'); setDate(meta?.business_today || ''); setError(''); setConfirm(false); setVisible(true);
        latest.current.onDraftClose();
    }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps -- событие открытия, снимок формы фиксируется один раз
    useEffect(() => {
        const timer = setInterval(() => setTick(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);
    useEffect(() => {
        setBindings(null); setBindingError('');
        if (!locationId) return undefined;
        const controller = new AbortController();
        payrollRequest(token, `finance/payroll/locations/${locationId}/history/`, { signal: controller.signal })
            .then(data => { if (!controller.signal.aborted) setBindings(data.bindings); })
            .catch(err => { if (!controller.signal.aborted) setBindingError(err.message); });
        return () => controller.abort();
    }, [token, locationId, meta?.revision, historyAttempt]);

    const phase = operation?.phase;
    useEffect(() => {
        onLockChange(!!phase && !['failed', 'rejected', 'succeeded'].includes(phase));
    }, [phase, onLockChange]);
    useEffect(() => () => onLockChange(false), [onLockChange]);

    function remember(next) {
        if (accountId == null) throw new Error('Не удалось определить аккаунт. Обновите страницу.');
        // Сохраняем тело ДО POST: после обрыва можно повторить тот же request_id.
        const stored = { version: 1, kind: next.kind, body: next.body, jobId: next.jobId,
            change: next.change, title: next.title };
        sessionStorage.setItem(storageKey, JSON.stringify(stored));
        if (alive.current) setOperation(next);
    }
    async function send(next) {
        if (busy.current) return;
        busy.current = true; setError('');
        try {
            remember({ ...next, phase: 'sending' });
            const response = await payrollRequest(token, `finance/payroll/${next.kind}/`, { method: 'POST', data: next.body });
            remember({ ...next, phase: 'polling', jobId: response.job_id });
        } catch (err) {
            if (alive.current) {
                setError(err.message);
                setOperation({ ...next, phase: err.uncertain || err.status === 423 || !err.status ? 'retry' : 'rejected' });
            }
        } finally { busy.current = false; }
    }
    useEffect(() => {
        if (operation?.phase !== 'polling' || !operation.jobId) return undefined;
        let cancelled = false, timer;
        const controller = new AbortController();
        async function poll() {
            try {
                const job = await payrollRequest(token, `finance/payroll/jobs/${operation.jobId}/`, { signal: controller.signal });
                if (cancelled) return;
                setError('');
                if (job.status === 'succeeded' || job.status === 'failed') {
                    const phase = job.status === 'failed' ? 'failed' : operation.kind === 'preview' ? 'ready' : 'succeeded';
                    setOperation({ ...operation, phase, job, change: job.change });
                    setConfirm(false);
                    if (phase === 'succeeded' && done.current !== job.id) {
                        done.current = job.id;
                        // Сбой повторной загрузки не превращает успешное применение в failed.
                        Promise.resolve(latest.current.onApplied()).catch(err => {
                            if (alive.current) setError(`Условия сохранены. Не удалось обновить экран: ${err.message}`);
                        });
                    }
                    return;
                }
                setOperation(current => current?.jobId === operation.jobId && current.phase === 'polling'
                    ? { ...current, job } : current);
            } catch (err) {
                if (cancelled) return;
                setError(err.message);
                if ([400, 401, 403, 404].includes(err.status)) {
                    setOperation({ ...operation, phase: 'status-error' });
                    return;
                }
            }
            if (!cancelled) timer = setTimeout(poll, 2000);
        }
        poll();
        return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
    }, [token, operation?.phase, operation?.jobId]); // eslint-disable-line react-hooks/exhaustive-deps -- новый опрос только при смене задания/фазы

    async function startPreview() {
        if (busy.current || !meta || !canEdit) return;
        if (mode === 'from_date' && !/^\d{4}-\d{2}-\d{2}$/.test(date)) { setError('Выберите дату начала.'); return; }
        busy.current = true; setError('');
        let next;
        try {
            if (operation.change.operation === 'rules') {
                if (!Object.keys(operation.change.patch).length) throw new Error('Выберите хотя бы один блок условий.');
                validatePatch(operation.change.patch);
            }
            const current = await payrollRequest(token, 'finance/payroll/meta/');
            if (current.revision !== operation.basis.revision || current.business_today !== operation.basis.business_today) {
                throw new Error('Данные или рабочий день изменились. Закройте окно, обновите данные и заново проверьте форму.');
            }
            // Фиксируем «сегодня» датой сервера: сетевой повтор завтра не сдвинет границу.
            const change = { ...operation.change, mode: mode === 'today' ? 'from_date' : mode };
            if (mode !== 'all') change.effective_from = mode === 'today' ? operation.basis.business_today : date;
            next = { ...operation, kind: 'preview', change, body: {
                request_id: newPayrollRequestId(), revision: operation.basis.revision, change,
            } };
        } catch (err) { setError(err.message); }
        finally { busy.current = false; }
        if (next) await send(next);
    }
    const expired = operation?.job?.finished_at
        ? tick >= Date.parse(operation.job.finished_at) + 15 * 60 * 1000 : false;
    function apply() {
        if (!canEdit || !confirm || expired || operation?.phase !== 'ready') return;
        try {
            send({ ...operation, kind: 'apply', job: undefined, jobId: undefined,
                body: { request_id: newPayrollRequestId(), preview_id: operation.jobId } });
        } catch (err) { setError(err.message); }
    }
    function close() {
        if (busy.current) return;
        if (RUNNING.includes(operation?.phase) || operation?.phase === 'status-error') {
            setVisible(false); return;
        }
        try { sessionStorage.removeItem(storageKey); }
        catch (err) { setError('Не удалось очистить локальный статус. Разрешите хранение данных сайта.'); return; }
        setOperation(null); setVisible(false); setError(''); setConfirm(false);
    }
    function bindingDraft() {
        if (!canEdit || !locationId || !configId || !meta || operation) return;
        setOperation({ phase: 'editing', basis: copy(meta), title: `Условия точки «${locations.find(l => String(l.id) === locationId)?.name || locationId}»`,
            change: { operation: 'binding', location_id: Number(locationId), config_id: configId === 'off' ? null : Number(configId) } });
        setMode('today'); setDate(meta.business_today); setConfirm(false); setError(''); setVisible(true);
    }
    return <Stack spacing={2} sx={{ ...payrollSurfaceSx, mb: 2 }}>
        {operation && <Alert color="primary">Есть {operation.kind === 'apply' ? 'применение' : 'изменение'} условий.
            <Button onClick={() => setVisible(true)}>Открыть</Button></Alert>}
        <Box component="details" sx={sectionSx}>
            <Typography component="summary" fontWeight="lg">Назначение условий точкам</Typography>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Typography level="body-sm">Для изменения одной точки создайте отдельный шаблон и назначьте его здесь. Общая конфигурация меняется сразу для всех использующих её точек.</Typography>
                <Select placeholder="Точка" value={locationId} onChange={(_, value) => setLocationId(value || '')}>
                    {locations.map(location => <Option key={location.id} value={String(location.id)}>{location.name}</Option>)}
                </Select>
                {bindingError && <Alert color="danger">{bindingError}<Button onClick={() => setHistoryAttempt(v => v + 1)}>Повторить</Button></Alert>}
                {bindings?.map(([day, id]) => <Typography key={day} level="body-sm">С {dayLabel(day)}: {id === null ? 'Автоматические условия отключены' : (configs.find(c => c.id === id) ? configLabel(configs.find(c => c.id === id)) : `Конфигурация №${id}`)}</Typography>)}
                {canEdit && <><Select placeholder="Назначить конфигурацию" value={configId} onChange={(_, value) => setConfigId(value || '')}>
                    <Option value="off">Отключить автоматические условия</Option>
                    {configs.map(config => <Option key={config.id} value={String(config.id)}>{configLabel(config)}</Option>)}
                </Select><Button disabled={!!operation || !meta || !bindings || !locationId || !configId} onClick={bindingDraft}>Выбрать дату и посмотреть расчёт</Button></>}
            </Stack>
        </Box>
        <Modal open={visible && !!operation} onClose={() => { if (!busy.current) close(); }}>
            <ModalDialog aria-labelledby="payroll-change-title" sx={dialogSx}>
                <ModalClose aria-label="Закрыть окно" onClick={close} />
                <Stack spacing={2}>
                    <Typography id="payroll-change-title" level="h4" sx={{ fontSize: 20, fontWeight: 800, pr: 4 }}>{operation?.title || 'Изменение условий'}</Typography>
                    {error && <Alert color="danger">{error}</Alert>}
                    {operation?.change?.operation === 'rules' && <RulesView payload={operation.change.patch} />}
                    {operation?.change?.operation === 'binding' && <Typography>Новые условия: {operation.change.config_id === null ? 'Отключить автоматику' : (configs.find(c => c.id === operation.change.config_id) ? configLabel(configs.find(c => c.id === operation.change.config_id)) : `Конфигурация №${operation.change.config_id}`)}</Typography>}
                    {operation?.phase === 'editing' && <>
                        {operation.availablePatch && <Stack spacing={1}>
                            <Typography>Какие действующие значения применить заново? Это позволяет отменить запланированное изменение выбранных блоков.</Typography>
                            {Object.entries(operation.availablePatch).map(([key, value]) => <Checkbox key={key}
                                label={FIELD_LABELS[key]} checked={Object.prototype.hasOwnProperty.call(operation.change.patch, key)}
                                onChange={event => {
                                    const checked = event.target.checked;
                                    setOperation(old => {
                                        const patch = { ...old.change.patch };
                                        if (checked) patch[key] = copy(value); else delete patch[key];
                                        return { ...old, change: { ...old.change, patch } };
                                    });
                                }} />)}
                        </Stack>}
                        <Typography>С какого дня применить?</Typography>
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                            {Object.entries(LABELS).map(([value, label]) => <Button key={value} variant={mode === value ? 'solid' : 'outlined'} aria-pressed={mode === value} sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, ...(mode === value ? { bgcolor: 'var(--joy-palette-primary-500)', color: '#fff', '&:hover': { bgcolor: 'var(--joy-palette-primary-600)' } } : {}) }} onClick={() => setMode(value)}>{label}</Button>)}
                        </Stack>
                        {mode === 'from_date' && <Input type="date" value={date} onChange={event => setDate(event.target.value)} slotProps={{ input: { min: '1000-01-01' } }} />}
                        <Typography level="body-sm">Рабочая дата формы: {operation?.basis?.business_today}. Москва, начало дня 06:00. Условия действуют с начала смены, включая выбранную дату.</Typography>
                        <Alert color={mode === 'all' ? 'warning' : 'neutral'}>{mode === 'all' ? 'Пересчитается вся история.' : 'До выбранной даты условия сохраняются.'} Переданные блоки заменят также их более поздние версии. Назначение точки заменит последующие привязки.</Alert>
                        <Button onClick={startPreview} disabled={!canEdit || !meta}>Рассчитать предпросмотр</Button>
                    </>}
                    {['sending', 'polling'].includes(operation?.phase) && <Box role="status" aria-live="polite" sx={{ p: 2, bgcolor: 'var(--joy-palette-background-level2)', borderRadius: '12px' }}>
                        <Typography fontWeight="lg">{operation.phase === 'sending' ? 'Отправляем запрос…'
                            : operation.job?.status === 'queued' ? 'В очереди — ожидает обработчика'
                            : operation.job?.status === 'running' ? (operation.kind === 'apply' ? 'Применяем условия…' : 'Рассчитываем предпросмотр…')
                            : 'Получаем статус задания…'}</Typography>
                        <LinearProgress aria-label="Ожидание завершения задания" sx={{ my: 1.5, '--LinearProgress-radius': '8px', '--LinearProgress-progressColor': 'var(--joy-palette-primary-500)', bgcolor: 'var(--joy-palette-background-level3)' }} />
                        <Typography level="body-sm">{operation.job?.status === 'queued'
                            ? 'Расчёт начнётся, когда обработчик возьмёт задание. Повторно отправлять запрос не нужно.'
                            : 'Можно свернуть окно. Задание продолжится, результат будет доступен здесь.'}</Typography>
                    </Box>}
                    {operation?.phase === 'retry' && <><Alert color="warning">Результат отправки не подтверждён. Повтор использует прежний идентификатор и не создаёт повторное применение.</Alert>
                        <Button onClick={() => send(operation)}>Повторить отправку</Button></>}
                    {operation?.phase === 'status-error' && <Button onClick={() => setOperation(old => ({ ...old, phase: 'polling' }))}>Проверить статус снова</Button>}
                    {['failed', 'rejected'].includes(operation?.phase) && <Alert color="danger">{operation.job?.error || 'Запрос отклонён.'} Изменение не применено. Закройте окно, обновите данные и создайте новый предпросмотр.</Alert>}
                    {['ready', 'succeeded'].includes(operation?.phase) && <>
                        <Typography>Начало действия: {dayLabel(operation.change.start || operation.change.effective_from)}.</Typography>
                        <ResultView result={operation.job.result} locations={locations} />
                    </>}
                    {operation?.phase === 'ready' && <>
                        {expired && <Alert color="warning">Прошло 15 минут. Нужен новый предпросмотр.</Alert>}
                        <Checkbox checked={confirm} onChange={event => setConfirm(event.target.checked)} label="Проверил даты, затронутые точки и суммы" />
                        <Button color="warning" disabled={!confirm || expired || !canEdit} onClick={apply}>Применить условия</Button>
                    </>}
                    {operation?.phase === 'succeeded' && <Alert color="success">Условия и начисления сохранены.</Alert>}
                    <Button variant="outlined" color="neutral" onClick={close}>
                        {RUNNING.includes(operation?.phase) || operation?.phase === 'status-error' ? 'Свернуть — задание продолжится' : 'Закрыть'}
                    </Button>
                    {['failed', 'rejected', 'succeeded'].includes(operation?.phase) && (
                        <Button
                            variant="plain"
                            onClick={() => {
                                close();
                                onReload();
                            }}
                        >
                            Закрыть и обновить условия
                        </Button>
                    )}
                </Stack>
            </ModalDialog>
        </Modal>
    </Stack>;
}